import { Download } from 'lucide-react'
import { ViewedIndicator } from './ViewedIndicator'
import { DownloadedIndicator } from './DownloadedIndicator'
import { resolveIcon } from '@/lib/documentTypeIcon'
import { Button } from '@/components/ui/button'
import type { DocumentSummary } from '@/lib/types/ged'

type Props = {
  doc: DocumentSummary
  showArea?: boolean
  onClick: (id: string) => void
  density?: 'compact' | 'default'
  isSelected?: boolean
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function FileRow({ doc, showArea = false, onClick, density = 'default', isSelected = false }: Props) {
  const Icon = resolveIcon(doc.typeIconName)
  const padY = density === 'compact' ? 10 : 14

  return (
    <div
      role="row"
      onClick={() => onClick(doc.id)}
      style={{
        display: 'grid',
        gridTemplateColumns: showArea
          ? '40px 1fr 140px 90px 56px 44px'
          : '40px 1fr 90px 56px 44px',
        alignItems: 'center',
        gap: 12,
        padding: `${padY}px 16px`,
        cursor: 'pointer',
        background: isSelected ? 'var(--ev-bg-active)' : 'transparent',
        borderLeft: isSelected ? '3px solid var(--ev-accent)' : '3px solid transparent',
        borderBottom: '1px solid var(--ev-divider)',
      }}
    >
      <div
        style={{
          width: 32,
          height: 36,
          background: doc.typeColorHex + '18',
          color: doc.typeColorHex,
          borderRadius: 4,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Icon size={16} />
      </div>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--fs-small)',
            fontWeight: 500,
            color: 'var(--ev-fg-strong)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {doc.name}
        </div>
        <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--ev-fg-muted)', marginTop: 2 }}>
          {doc.typeName}
        </div>
      </div>

      {showArea && (
        <div
          style={{
            fontSize: 'var(--fs-small)',
            color: 'var(--ev-fg-muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {doc.areaName}
        </div>
      )}

      <div
        style={{
          fontSize: 'var(--fs-micro)',
          color: 'var(--ev-fg-muted)',
          textAlign: 'right',
          fontFamily: 'ui-monospace, monospace',
        }}
      >
        {formatBytes(doc.fileSizeBytes)}
      </div>

      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
        <ViewedIndicator viewedAt={doc.viewedAt} />
        <DownloadedIndicator downloadedAt={doc.downloadedAt} />
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation()
          onClick(doc.id)
        }}
        title="Baixar (abre detalhes)"
        aria-label="Baixar"
        style={{ color: 'var(--ev-fg-muted)', width: 30, height: 30 }}
      >
        <Download size={14} />
      </Button>
    </div>
  )
}
